import React, { useState, useEffect, useCallback } from 'react';
import { RoleDefinition, TabPermissions } from '../../types';
import { getRoles, createRole, updateRole, deleteRole, getUsers } from '../../services/systemApi';
import { ConflictError } from '../../services/systemApi';

const PERMISSION_KEYS: { key: keyof TabPermissions; label: string }[] = [
  { key: 'logs', label: 'Logs' },
  { key: 'monitoring', label: 'Monitoring' },
  { key: 'configuration', label: 'Configuration' },
  { key: 'system', label: 'System' },
];

const EMPTY_PERMISSIONS: TabPermissions = {
  logs: false,
  monitoring: false,
  configuration: false,
  system: false,
};

const RoleManagementTab: React.FC = () => {
  const [roles, setRoles] = useState<Record<string, RoleDefinition>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newPermissions, setNewPermissions] = useState<TabPermissions>({ ...EMPTY_PERMISSIONS });
  const [createError, setCreateError] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteAffectedCount, setDeleteAffectedCount] = useState<number>(0);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRoles();
      setRoles(data);
    } catch (err: any) {
      setError(err.message || 'Unable to load roles. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleCreate = async () => {
    clearMessages();
    setCreateError(null);

    const trimmedName = newRoleName.trim();
    if (!trimmedName) {
      setCreateError('Role name is required');
      return;
    }

    setSaving(true);
    try {
      await createRole(trimmedName, newPermissions);
      setSuccess(`Role "${trimmedName}" created successfully.`);
      setShowCreateForm(false);
      setNewRoleName('');
      setNewPermissions({ ...EMPTY_PERMISSIONS });
      setCreateError(null);
      await loadRoles();
    } catch (err: any) {
      if (err instanceof ConflictError) {
        setCreateError('A role with this name already exists.');
      } else {
        setError(err.message || 'Failed to create role.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionToggle = async (roleName: string, permKey: keyof TabPermissions) => {
    clearMessages();
    const role = roles[roleName];
    if (!role) return;

    const updatedPermissions: TabPermissions = {
      ...role.permissions,
      [permKey]: !role.permissions[permKey],
    };

    setSaving(true);
    try {
      await updateRole(roleName, updatedPermissions);
      setRoles((prev) => ({
        ...prev,
        [roleName]: { ...prev[roleName], permissions: updatedPermissions },
      }));
      setSuccess(`Role "${roleName}" updated successfully.`);
    } catch (err: any) {
      if (err instanceof ConflictError) {
        setError('Role was modified by another user. Please reload and try again.');
      } else {
        setError(err.message || 'Failed to update role.');
      }
      // Reload to get fresh state
      await loadRoles();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = async (roleName: string) => {
    clearMessages();
    setDeleteLoading(true);
    try {
      const users = await getUsers();
      const affected = users.filter((u) => u.roleName === roleName).length;
      setDeleteAffectedCount(affected);
      setDeleteConfirm(roleName);
    } catch {
      // If we can't fetch users, still allow delete but show 0
      setDeleteAffectedCount(0);
      setDeleteConfirm(roleName);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    clearMessages();
    setSaving(true);
    try {
      await deleteRole(deleteConfirm);
      setSuccess(`Role "${deleteConfirm}" deleted successfully.`);
      setDeleteConfirm(null);
      await loadRoles();
    } catch (err: any) {
      setError(err.message || 'Failed to delete role.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewRoleName('');
    setNewPermissions({ ...EMPTY_PERMISSIONS });
    setCreateError(null);
  };

  const roleEntries = Object.entries(roles).sort(([a], [b]) => a.localeCompare(b));

  if (loading && Object.keys(roles).length === 0) {
    return <div className="text-gray-500">Loading roles...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center justify-between" role="alert">
          <span>{error}</span>
          <button
            onClick={() => { setError(null); loadRoles(); }}
            className="ml-4 text-sm font-medium text-red-700 underline hover:text-red-900"
          >
            Retry
          </button>
        </div>
      )}

      {/* Success banner */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center justify-between" role="status">
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="ml-4 text-sm font-medium text-green-700 hover:text-green-900"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Header with Add New button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Roles</h3>
        {!showCreateForm && (
          <button
            type="button"
            onClick={() => { clearMessages(); setShowCreateForm(true); }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Add New
          </button>
        )}
      </div>

      {/* Create role form */}
      {showCreateForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Create New Role</h4>
          <div className="space-y-4">
            <div>
              <label htmlFor="role-name" className="block text-sm font-medium text-gray-700">Role Name</label>
              <input
                id="role-name"
                type="text"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                className={`mt-1 block w-full max-w-md rounded-md shadow-sm sm:text-sm ${
                  createError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="e.g. Operator"
              />
              {createError && <p className="mt-1 text-sm text-red-600">{createError}</p>}
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">Tab Permissions</span>
              <div className="flex flex-wrap gap-4">
                {PERMISSION_KEYS.map(({ key, label }) => (
                  <label key={key} className="inline-flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={newPermissions[key]}
                      onChange={() => setNewPermissions((prev) => ({ ...prev, [key]: !prev[key] }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 flex space-x-3">
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancelCreate}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Roles table */}
      {roleEntries.length === 0 && !showCreateForm ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No roles configured. Click "Add New" to create one.
        </div>
      ) : (
        roleEntries.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role Name
                  </th>
                  {PERMISSION_KEYS.map(({ key, label }) => (
                    <th key={key} scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {label}
                    </th>
                  ))}
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {roleEntries.map(([name, role]) => (
                  <React.Fragment key={name}>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {name}
                      </td>
                      {PERMISSION_KEYS.map(({ key }) => (
                        <td key={key} className="px-6 py-4 whitespace-nowrap text-center">
                          <input
                            type="checkbox"
                            checked={role.permissions[key]}
                            onChange={() => handlePermissionToggle(name, key)}
                            disabled={saving}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                            aria-label={`${key} permission for ${name}`}
                          />
                        </td>
                      ))}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(name)}
                          disabled={saving || deleteLoading}
                          className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {/* Delete confirmation */}
                    {deleteConfirm === name && (
                      <tr>
                        <td colSpan={PERMISSION_KEYS.length + 2} className="px-6 py-3">
                          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md" role="alert">
                            {deleteAffectedCount > 0 ? (
                              <p className="text-sm">
                                This role is currently assigned to <span className="font-medium">{deleteAffectedCount}</span> user{deleteAffectedCount !== 1 ? 's' : ''}.
                                Are you sure you want to delete <span className="font-medium">{name}</span>?
                              </p>
                            ) : (
                              <p className="text-sm">
                                Are you sure you want to delete <span className="font-medium">{name}</span>?
                              </p>
                            )}
                            <div className="mt-2 flex space-x-3">
                              <button
                                type="button"
                                onClick={handleDeleteConfirm}
                                disabled={saving}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                              >
                                {saving ? 'Deleting...' : 'Confirm Delete'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteConfirm(null)}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default RoleManagementTab;
